import { Box, SxProps, Theme } from '@mui/material';
import 'dayjs/locale/it';
import { ToolbarAction, ToolbarFilter } from '../../../types/toolbar.types';
import { ActionSection } from './DataGridActionsToolbar';
import { FilterSectionRow } from './DataGridFilterToolbar';

/**
 * Property interface definitions processed by the `GenericActivityToolbar` component context.
 */
interface ConfigurableToolbarProps {
  /**
   * Core data service instance containing integration API endpoints.
   * Typically manages specialized backend operations such as file imports, bulk updates, and asynchronous data exports.
   * @type {any}
   */
  service?: any;
  /**
   * Additional query parameters, filter states, or context variables sent from the parent container.
   * Automatically passed downstream to backend API calls (e.g., entity IDs, structural tenant configurations).
   * @type {Record<string, unknown>}
   */
  outerParams?: Record<string, unknown>;
  entityId?: number;
  /**
   * Flag to restrict data extraction utilities across the interface.
   * When set to `true`, all primary data download and spreadsheet export features are completely disabled.
   * @type {boolean}
   * @default false
   */
  disableExport?: boolean;
  /**
   * When enabled, automatically mounts native DataGrid layout buttons (Columns/Filters) ahead of the filter array.
   * @default true
   */
  showGridControls?: boolean;
  /** Dynamic array containing data filter configurations rendered on the left margin */
  filters?: ToolbarFilter[];
  /** Dynamic array containing functional action setups rendered on the right margin */
  actions?: ToolbarAction[];
  /** System style override object leveraging Material-UI theme layout engines */
  sx?: SxProps<Theme>;
}

/**
 * `ConfigurableToolbar` is an abstract, configuration-driven toolbar component
 * designed to serve as a universal control bar sitting above data table views (DataGrid).
 *
 * The layout distribution split handles twin responsive sections symmetrically:
 * - **Left Hand Section**: Builds custom database parameters selection nodes (text filters, dropdown selections, calendar components).
 * - **Right Hand Section**: Renders operations handles (data exports, registration triggers) sandboxed behind role-based permission flags.
 *
 * It embeds an advanced webkit indicator masking stylesheet overlay for `date` inputs,
 * replacing disparate browser calendar nodes with a single, crisp Material-UI element across all screen displays.
 *
 * @component
 * @example
 * ```tsx
 * <ConfigurableToolbar
 *   filters={[{ id: 'search', type: 'text', label: 'Search records...', value: search, onChange: setSearch }]}
 *   actions={[{ id: 'add', label: 'New Activity', onClick: handleOpen, startIcon: <AddIcon /> }]}
 * />
 * ```
 */
export const ConfigurableToolbar = ({
  service,
  outerParams,
  entityId,
  disableExport,
  showGridControls,
  filters = [],
  actions = [],
  sx
}: ConfigurableToolbarProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // p: 2,
        pb: 1,
        width: '100%',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 2,
        ...sx
      }}>
      {/* LEFT SECTION: Dynamic Filter Generation Row */}

      <FilterSectionRow filters={filters} showGridControls={showGridControls} />

      {/* RIGHT SECTION: Dynamic Operations Button Actions */}

      <ActionSection
        service={service}
        actions={actions}
        outerParams={outerParams}
        entityId={entityId}
        disableExport={disableExport}
      />
    </Box>
  );
};
